import {
  Button,
  FileInput,
  HelperText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from 'flowbite-react';
import { type ChangeEvent, type FormEvent, useEffect, useState } from 'react';
import { FiAlertTriangle, FiTrash2 } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { useClientQuery } from '../../queries/use-client.js';
import { useDeleteClientMutation } from '../../queries/use-delete-client.js';
import { useUpdateClientMutation } from '../../queries/use-update-client.js';
import { useUploadTenantAssetMutation } from '../../queries/use-upload-tenant-asset.js';

/**
 * Settings sub-tab — edit form for the active tenant.
 *
 * Mirrors the legacy `wf-adminconsole/app/clients/[id]/settings`:
 * Name + Email are wired to `PUT /v1/clients/{id}` (Casbin
 * `client:write`, Admin / Operator). Icon + Logo upload through the
 * presigned-PUT flow:
 *   1. browser asks the API for a presigned URL (Casbin gate),
 *   2. browser PUTs the file directly to S3,
 *   3. browser calls `PUT /v1/clients/{id}` with the new public URL.
 * The trash button calls `DELETE /v1/clients/{id}` (Casbin
 * `client:delete`, Admin only) after a confirmation modal —
 * destructive ops shouldn't be one-click.
 */
export function ClientSettings(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, error, isLoading, isError } = useClientQuery(id);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [iconError, setIconError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);

  const update = useUpdateClientMutation();
  const del = useDeleteClientMutation();
  const upload = useUploadTenantAssetMutation();

  // Sync the form with whichever client is currently loaded so navigating
  // between tenants doesn't carry the previous tenant's edits.
  useEffect(() => {
    if (data) {
      setName(data.name);
      setEmail(data.email ?? '');
      setIconError(null);
      setLogoError(null);
      update.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  if (isLoading) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400" role="status">
        Loading client…
      </p>
    );
  }
  if (isError) {
    return (
      <div
        role="alert"
        className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
      >
        {error instanceof Error ? error.message : 'Failed to load client'}
      </div>
    );
  }
  if (!data || !id) return <></>;

  const dirty =
    name.trim() !== data.name || (email.trim() === '' ? null : email.trim()) !== data.email;
  const canSubmit = dirty && name.trim() !== '' && !update.isPending;

  const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    update.mutate({
      id,
      patch: {
        name: name.trim(),
        email: email.trim() === '' ? null : email.trim(),
      },
    });
  };

  const handleDeleteOpen = (): void => {
    setDeleteConfirmText('');
    del.reset();
    setDeleteOpen(true);
  };

  const handleDeleteClose = (): void => {
    if (del.isPending) return;
    setDeleteOpen(false);
  };

  const handleDeleteConfirm = (): void => {
    del.mutate(id, {
      onSuccess: () => {
        setDeleteOpen(false);
        navigate('/clients', { replace: true });
      },
    });
  };

  // Type-to-confirm: operator must retype the tenant name verbatim
  // before the Delete button enables. Matches GitHub's repo-delete UX
  // — destructive cross-tier wipe shouldn't be one click + one Yes.
  const deleteArmed = deleteConfirmText.trim() === data?.name;

  const uploadAndPersist = async (
    kind: 'icon' | 'logo',
    file: File,
    setError: (msg: string | null) => void,
  ): Promise<void> => {
    setError(null);
    try {
      const { publicUrl } = await upload.mutateAsync({ clientId: id, kind, file });
      // Persist the new URL on the client row. Use mutateAsync so any
      // server-side error (zod, 409 etc.) bubbles up here for display.
      await update.mutateAsync({
        id,
        patch: kind === 'icon' ? { smallLogoUrl: publicUrl } : { largeLogoUrl: publicUrl },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    }
  };

  const handleIconChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) void uploadAndPersist('icon', file, setIconError);
    e.target.value = '';
  };
  const handleLogoChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) void uploadAndPersist('logo', file, setLogoError);
    e.target.value = '';
  };

  return (
    <>
      <form
        onSubmit={handleSubmit}
        aria-label="Edit client settings"
        className="max-w-2xl rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800"
      >
        <div className="space-y-5">
          <Field label="Name" htmlFor="settings-name">
            <TextInput
              id="settings-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={update.isPending}
            />
          </Field>

          <Field label="Email" htmlFor="settings-email">
            <TextInput
              id="settings-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={update.isPending}
            />
          </Field>

          <Field
            label="Icon"
            htmlFor="settings-icon"
            hint="64px × 64px PNG or JPG, max 1 MB. Stored as smallLogoUrl on the tenant record."
          >
            <div className="flex items-center gap-3">
              <FileInput
                id="settings-icon"
                accept="image/png,image/jpeg"
                onChange={handleIconChange}
                disabled={upload.isPending}
              />
              <LogoPreview src={data.smallLogoUrl} size={32} />
            </div>
            {iconError ? (
              <p role="alert" className="mt-2 text-xs text-red-700 dark:text-red-400">
                {iconError}
              </p>
            ) : null}
          </Field>

          <Field
            label="Logo"
            htmlFor="settings-logo"
            hint="512px × 512px PNG or JPG, max 5 MB. Stored as largeLogoUrl on the tenant record."
          >
            <div className="flex items-center gap-3">
              <FileInput
                id="settings-logo"
                accept="image/png,image/jpeg"
                onChange={handleLogoChange}
                disabled={upload.isPending}
              />
              <LogoPreview src={data.largeLogoUrl} size={48} />
            </div>
            {logoError ? (
              <p role="alert" className="mt-2 text-xs text-red-700 dark:text-red-400">
                {logoError}
              </p>
            ) : null}
          </Field>

          {update.isError ? (
            <div
              role="alert"
              className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
            >
              {update.error instanceof Error ? update.error.message : 'Failed to save changes'}
            </div>
          ) : null}
          {update.isSuccess ? (
            <div
              role="status"
              className="rounded-md border border-green-300 bg-green-50 p-3 text-sm text-green-900 dark:border-green-700 dark:bg-green-950 dark:text-green-200"
            >
              Changes saved.
            </div>
          ) : null}

          <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
            <Button type="submit" disabled={!canSubmit}>
              {update.isPending ? 'Saving…' : 'Update'}
            </Button>
          </div>
        </div>
      </form>

      <DangerZone
        clientName={data.name}
        onDelete={handleDeleteOpen}
        deletePending={del.isPending}
      />

      <Modal show={deleteOpen} onClose={handleDeleteClose} dismissible={!del.isPending} size="lg">
        <ModalHeader className="border-b border-red-200 dark:border-red-900">
          <span className="flex items-center gap-2 text-red-700 dark:text-red-400">
            <FiAlertTriangle aria-hidden="true" />
            Permanently delete this tenant?
          </span>
        </ModalHeader>
        <ModalBody>
          <div
            role="alert"
            className="mb-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
          >
            <p className="font-semibold">This action cannot be undone.</p>
            <p className="mt-1">
              The following will be permanently destroyed for{' '}
              <span className="font-semibold">{data.name}</span>:
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
              <li>
                Postgres rows: OAuth apps, Workfront connections, Fusion connections, licenses, and
                every scenario / KPI / tag / workfront-object that referenced them (cascade).
              </li>
              <li>
                Secrets Manager bundles for the client + every license / workfront-config /
                fusion-config / oauth-app — force-deleted with{' '}
                <span className="font-mono">no recovery window</span>.
              </li>
              <li>
                Engine DynamoDB rows (system logs, day logs, KPI rollups) partitioned on each
                license id.
              </li>
              <li>
                S3 objects: blueprints, screenshots, planning backups under every former license id;
                tenant logos under <span className="font-mono">tenant-assets/{data.id}/</span>.
              </li>
            </ul>
          </div>

          <p className="mb-2 text-sm text-gray-700 dark:text-gray-300">
            Type{' '}
            <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs dark:bg-gray-800">
              {data.name}
            </code>{' '}
            below to enable the Delete button.
          </p>
          <TextInput
            id="delete-confirm-name"
            type="text"
            autoComplete="off"
            placeholder={data.name}
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            disabled={del.isPending}
            aria-label="Type tenant name to confirm deletion"
          />

          {del.isError ? (
            <div
              role="alert"
              className="mt-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-700 dark:bg-red-950 dark:text-red-200"
            >
              {del.error instanceof Error ? del.error.message : 'Failed to delete'}
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="gray" onClick={handleDeleteClose} disabled={del.isPending}>
            Cancel
          </Button>
          <Button
            color="failure"
            onClick={handleDeleteConfirm}
            disabled={!deleteArmed || del.isPending}
          >
            <FiTrash2 className="mr-2" aria-hidden="true" />
            {del.isPending ? 'Deleting…' : 'I understand — delete tenant'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

/**
 * Dedicated bordered section below the main settings form. Visually
 * separated from edit / upload affordances so the operator's eye lands
 * on the destructive surface only when they're looking for it.
 *
 * GitHub-style: red border, red label, brief consequence summary, and
 * the actual Delete button rendered as a labeled red button (not a bare
 * trash icon next to Update — see commit history for the prior shape).
 */
function DangerZone({
  clientName,
  onDelete,
  deletePending,
}: {
  clientName: string;
  onDelete: () => void;
  deletePending: boolean;
}): JSX.Element {
  return (
    <section
      aria-labelledby="danger-zone-heading"
      className="mt-8 max-w-2xl rounded-lg border-2 border-red-300 bg-white shadow-sm dark:border-red-800 dark:bg-gray-800"
    >
      <header className="flex items-center gap-2 rounded-t-lg border-b border-red-200 bg-red-50 px-6 py-3 dark:border-red-900 dark:bg-red-950/40">
        <FiAlertTriangle className="text-red-700 dark:text-red-400" aria-hidden="true" />
        <h3
          id="danger-zone-heading"
          className="text-sm font-semibold uppercase tracking-wide text-red-700 dark:text-red-400"
        >
          Danger zone
        </h3>
      </header>
      <div className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Delete tenant</p>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            Wipes the <span className="font-semibold">{clientName}</span> tenant and every owned
            artifact across Postgres, Secrets Manager, DynamoDB, and S3. Force-deletes with no
            recovery window — cannot be undone.
          </p>
        </div>
        <Button
          type="button"
          color="failure"
          onClick={onDelete}
          disabled={deletePending}
          aria-label={`Delete ${clientName}`}
          className="shrink-0"
        >
          <FiTrash2 className="mr-2" aria-hidden="true" />
          Delete tenant…
        </Button>
      </div>
    </section>
  );
}

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <div className="mb-1 block">
        <Label htmlFor={htmlFor}>{label}</Label>
      </div>
      {children}
      {hint ? <HelperText className="mt-1">{hint}</HelperText> : null}
    </div>
  );
}

function LogoPreview({ src, size }: { src: string | null; size: number }): JSX.Element {
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-md border border-gray-300 bg-gray-100 dark:border-gray-600 dark:bg-gray-700"
      style={{ width: size, height: size }}
    >
      {src ? (
        <img
          src={src}
          alt=""
          className="h-full w-full rounded-md object-contain"
          width={size}
          height={size}
        />
      ) : (
        <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
      )}
    </div>
  );
}
